import { AlertTriangle, Check, Copy } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

export interface KycLinkRevealProps {
  /** Verification URL to share with the client. */
  link: string;
  /** ISO date the link expires at; renders a validity hint when provided. */
  expiresAt?: string | null;
  className?: string;
}

/**
 * Secret-reveal block for a KYC verification link — same pattern as the RBAC API-key post-creation banner:
 * warning-tinted card, monospace read-only field and a copy button whose icon toggles to a check for ~2s.
 */
export function KycLinkReveal({ link, expiresAt, className }: KycLinkRevealProps) {
  const { t } = useTranslations("clients");
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    },
    [],
  );

  const handleCopy = useCallback(() => {
    navigator.clipboard
      .writeText(link)
      .then(() => {
        setCopied(true);
        if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  }, [link]);

  const expiresLabel = expiresAt ? new Date(expiresAt).toLocaleString() : null;

  return (
    <div className={cn("rounded-md border border-warning/40 bg-warning/10 p-3.5", className)}>
      <div className="flex items-center gap-2 text-[13px] font-semibold text-foreground">
        <AlertTriangle size={14} className="shrink-0 text-warning" />
        {t("kyc_link_title")}
      </div>
      <p className="mt-1 text-[12px] text-muted-foreground">
        {expiresLabel ? t("kyc_link_hint_expires", { date: expiresLabel }) : t("kyc_link_hint")}
      </p>
      <div className="mt-2.5 flex items-center gap-2">
        <input
          readOnly
          value={link}
          onFocus={(event) => event.target.select()}
          className="min-w-0 flex-1 rounded-md border border-border bg-background px-2.5 py-2 font-mono text-[12px] text-foreground outline-none"
          aria-label={t("kyc_link_title")}
        />
        <ColocaIconButton
          type="button"
          size="sm"
          icon={copied ? Check : Copy}
          aria-label={copied ? t("kyc_link_copied") : t("kyc_link_copy")}
          onClick={handleCopy}
        />
      </div>
    </div>
  );
}
